import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, type JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Code2, Pencil, Radical } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/utils.js';
import { MathInlineNode } from './math-inline-node.js';
import { Textarea } from './textarea.js';

// Matches inline `$...$` and block `$$...$$` LaTeX segments — the delimiter convention this
// codebase's remark-math/rehype-katex rendering pipeline expects (see src/db/prompts.ts).
// Doesn't handle escaped `\$` or `$` used as a currency sign; good enough for the LLM-generated
// and teacher-edited content this field actually contains.
const MATH_SEGMENT_REGEX = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;

function parseLineToInline(line: string): JSONContent[] {
  const nodes: JSONContent[] = [];
  MATH_SEGMENT_REGEX.lastIndex = 0;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MATH_SEGMENT_REGEX.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', text: line.slice(lastIndex, match.index) });
    }
    const isBlock = match[1] !== undefined;
    nodes.push({ type: 'mathInline', attrs: { latex: (isBlock ? match[1] : match[2]) ?? '', isBlock } });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) {
    nodes.push({ type: 'text', text: line.slice(lastIndex) });
  }
  return nodes;
}

function parseToDoc(text: string): JSONContent {
  return {
    type: 'doc',
    content: text.split('\n').map(line => ({ type: 'paragraph', content: parseLineToInline(line) })),
  };
}

function serializeInlineNode(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'mathInline') {
    const delimiter = node.attrs?.isBlock ? '$$' : '$';
    return `${delimiter}${(node.attrs?.latex as string) ?? ''}${delimiter}`;
  }
  return '';
}

function serializeDoc(doc: JSONContent): string {
  return (doc.content ?? [])
    .map(paragraph => (paragraph.content ?? []).map(serializeInlineNode).join(''))
    .join('\n');
}

interface MinimalKeyboardEvent {
  key: string;
  shiftKey: boolean;
  preventDefault(): void;
  stopPropagation(): void;
}

export interface LatexRichEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onKeyDown?: (event: MinimalKeyboardEvent) => void;
  /** Exam this editor belongs to — forwarded to MathInlineNode so nlToLatex cost tracking can attribute the call. */
  examId?: string;
}

/**
 * Editor for markdown text with embedded `$...$` / `$$...$$` LaTeX segments (the shape of fields
 * like the master solution and grading criteria — free text, not a single formula). Formulas are
 * edited in place as WYSIWYG MathLive fields directly inside the running text, no modal — the
 * "Formula" button inserts a new one at the cursor, and each formula gets its own small inline
 * toolbar (AI/natural-language and delete) on hover/focus. A raw-text toggle remains for power users.
 */
export function LatexRichEditor({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  onKeyDown,
  examId,
}: LatexRichEditorProps) {
  const { t } = useTranslation();
  const [isRawMode, setIsRawMode] = React.useState(false);
  const lastSerialized = React.useRef(value);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        underline: false,
        code: false,
        link: false,
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: false,
        listKeymap: false,
      }),
      MathInlineNode.configure({ examId }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: parseToDoc(value),
    onUpdate: ({ editor: e }) => {
      const serialized = serializeDoc(e.getJSON());
      lastSerialized.current = serialized;
      onChange(serialized);
    },
    editorProps: {
      handleKeyDown: (_view, event) => {
        onKeyDown?.(event);
        return event.defaultPrevented;
      },
    },
  });

  // Keep the editor in sync when `value` changes from outside (e.g. after loading a different
  // task, or a raw-mode edit). Skip when the change originated from this editor's own onUpdate.
  React.useEffect(() => {
    if (!editor || isRawMode || value === lastSerialized.current) return;
    lastSerialized.current = value;
    editor.commands.setContent(parseToDoc(value));
  }, [editor, isRawMode, value]);

  React.useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const insertFormula = () => {
    editor?.chain().focus().insertContent({ type: 'mathInline', attrs: { latex: '', isBlock: false } }).run();
  };

  const switchToRawMode = () => {
    if (editor) lastSerialized.current = serializeDoc(editor.getJSON());
    setIsRawMode(true);
  };

  const switchToVisualMode = () => {
    setIsRawMode(false);
    if (editor && value !== lastSerialized.current) {
      lastSerialized.current = value;
      editor.commands.setContent(parseToDoc(value));
    }
  };

  return (
    <div className={cn('flex flex-col rounded-md border border-input bg-background', className)}>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-input px-2 py-1">
        <button
          type="button"
          onClick={insertFormula}
          disabled={disabled || isRawMode}
          title={t('LatexEditor.insertFormulaHint')}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-300 dark:hover:bg-brand-900/20"
        >
          <Radical size={14} />
          {t('LatexEditor.insertFormula')}
        </button>

        <button
          type="button"
          aria-pressed={isRawMode}
          aria-label={isRawMode ? t('LatexEditor.switchToVisualEditor') : t('LatexEditor.switchToRawLatex')}
          title={isRawMode ? t('LatexEditor.switchToVisualEditor') : t('LatexEditor.switchToRawLatex')}
          onClick={() => (isRawMode ? switchToVisualMode() : switchToRawMode())}
          disabled={disabled}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md border transition-colors disabled:opacity-50',
            isRawMode
              ? 'border-brand-300 bg-brand-100 text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
              : 'border-transparent text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
          )}
        >
          {isRawMode ? <Pencil size={14} /> : <Code2 size={14} />}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {isRawMode ? (
          <Textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="h-full w-full resize-none rounded-none border-none font-mono text-xs focus-visible:ring-0"
          />
        ) : (
          <EditorContent
            editor={editor}
            className="prose prose-sm h-full max-w-none p-3 text-sm leading-relaxed dark:prose-invert [&_.ProseMirror]:h-full [&_.ProseMirror]:outline-none"
          />
        )}
      </div>
    </div>
  );
}
